/**
 * Upload, capture, and video duration limits by plan.
 *
 * Successful completions are tracked in `users/{uid}/billing/metrics.captureTotals.totalCaptures`
 * (incremented on first transition to processing.status === "completed"). Failed pipelines increment
 * `captureTotals.totalFailedCaptures` on first transition to "failed".
 *
 * Free tier: one lifetime capture + concurrent in-flight captures (see hooks/useProjects).
 * Capture packs: prepaid remaining in billing/metrics.capturePacks (never expire until used).
 * Enterprise: no monthly capture cap in-app (fair use).
 */

import {
  isShopifyPlan,
  resolveShopifyPlanId,
  shopifyPlanMaxVideoDurationSec,
} from './shopifyPlans';
import { canUseBrandedDeliveryForProject } from './projectDeliveryUnlock';

/**
 * Per-file byte ceiling for source video uploads (abuse / infra guard).
 * Plan differentiation is by video duration (getMaxVideoDurationSecondsForPlan), not these caps.
 */
export const SOURCE_VIDEO_UPLOAD_MAX_BYTES = 10 * 1024 * 1024 * 1024; // 10 GiB — per-upload file size ceiling (all tiers)
export const SOURCE_VIDEO_UPLOAD_MAX_BYTES_ENTERPRISE = 10 * 1024 * 1024 * 1024; // 10 GiB

/**
 * Lifetime free captures (matches pricing bullets). No time limit. Server-enforced.
 */
export const FREE_PLAN_CAPTURE_LIMIT = 1;

/** Ops-granted extra lifetime captures (`billing/metrics.captureQuota.lifetimeBonus`). */
export function readLifetimeCaptureBonus(metrics) {
  const raw = metrics?.captureQuota?.lifetimeBonus;
  const bonus = Number(raw ?? 0);
  return Number.isFinite(bonus) && bonus > 0 ? Math.floor(bonus) : 0;
}

export function freePlanLifetimeCaptureLimit(metrics) {
  return FREE_PLAN_CAPTURE_LIMIT + readLifetimeCaptureBonus(metrics);
}

/** Server-side concurrent processing cap per account (queued + processing). */
export const MAX_CONCURRENT_PROCESSING_PER_ACCOUNT = 2;

export function getConcurrentProcessingQueueNotice(processingCount) {
  if (!Number.isFinite(processingCount) || processingCount < MAX_CONCURRENT_PROCESSING_PER_ACCOUNT) {
    return null;
  }
  return 'Upload accepted. Waiting for the next available GPU slot.';
}

export const PROFESSIONAL_MONTHLY_CAPTURE_LIMIT = 15;
export const STUDIO_MONTHLY_CAPTURE_LIMIT = 30;

export const ROLLING_CAPTURE_PERIOD_MS = 30 * 24 * 60 * 60 * 1000;

/** Free tier max source video length (5 minutes). Keep in sync with functions/src/shared.ts */
export const FREE_PLAN_MAX_VIDEO_DURATION_SEC = 300;
/** All paid tiers (capture pack, studio, enterprise, Shopify paid). Keep in sync with functions/src/shared.ts */
export const PAID_PLAN_MAX_VIDEO_DURATION_SEC = 900;
/** @deprecated Use PAID_PLAN_MAX_VIDEO_DURATION_SEC — kept for imports that reference studio/enterprise caps */
export const STUDIO_MAX_VIDEO_DURATION_SEC = PAID_PLAN_MAX_VIDEO_DURATION_SEC;
export const ENTERPRISE_MAX_VIDEO_DURATION_SEC = PAID_PLAN_MAX_VIDEO_DURATION_SEC;
export const PROFESSIONAL_MAX_VIDEO_DURATION_SEC = PAID_PLAN_MAX_VIDEO_DURATION_SEC;

/** Total capture-tree storage caps (matches billing/metrics.storage.capBytes policy). */
export const FREE_STORAGE_CAP_BYTES = 25 * 1024 * 1024 * 1024;
export const PROFESSIONAL_STORAGE_CAP_BYTES = 50 * 1024 * 1024 * 1024;
export const STUDIO_STORAGE_CAP_BYTES = 100 * 1024 * 1024 * 1024;
export const ENTERPRISE_STORAGE_CAP_BYTES = 100 * 1024 * 1024 * 1024;

/** Normalize Firestore `subscription.plan` to canonical keys used in the app. */
export function normalizeSubscriptionPlan(plan) {
  const p = String(plan ?? 'free')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  if (p === 'pro_delivery') return 'pro_delivery';
  if (p === 'pro' || p === 'professional' || p === 'studio' || p === 'pay_per_capture') {
    return 'capture_pack';
  }
  if (p === 'unlimited') return 'enterprise';
  return p;
}

/** @deprecated Legacy pay-per-capture plan id. Main site uses prepaid capture packs. */
export function isPayPerCapturePlan(plan) {
  const raw = String(plan ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  return raw === 'pay_per_capture';
}

/** Prepaid capture pack billing — professional entitlements, no monthly cap. */
export function isCapturePackPlan(plan) {
  return normalizeSubscriptionPlan(plan) === 'capture_pack';
}

/** Professional-tier feature gates (video length, storage, share links, etc.). Excludes team collaboration. */
export function hasProfessionalEntitlements(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return p === 'capture_pack' || p === 'professional' || p === 'pay_per_capture';
}

/** Project team invites, editor seats, and seat add-ons (requires at least one paid capture purchase). */
export function canUseTeamCollaboration(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return (
    p === 'capture_pack' ||
    p === 'professional' ||
    p === 'studio' ||
    p === 'enterprise'
  );
}

/** Multi-capture project share links (watermarked until project delivery unlock). */
export function canUseProjectShare(_plan) {
  return true;
}

/** Curated timeline share links — paid capture pack or higher. */
export function canUseTimelineShare(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return hasProfessionalEntitlements(plan) || p === 'studio' || p === 'enterprise' || p === 'pro_delivery';
}

/** Branded client delivery portals (logo, accent) — per-project unlock or Enterprise. */
export function canUseBrandedClientPortal(plan, projectDoc = null) {
  if (projectDoc) return canUseBrandedDeliveryForProject(normalizeSubscriptionPlan(plan), projectDoc);
  const p = normalizeSubscriptionPlan(plan);
  return p === 'pro_delivery' || p === 'enterprise';
}

/** Downloadable damage / incident PDF reports — unlocked project or Enterprise. */
export function canDownloadDamageReport(plan, projectDoc = null) {
  return canUseBrandedClientPortal(plan, projectDoc);
}

/** Branded damage reports (operator logo, no Volugraph footer). */
export function canUseBrandedDamageReport(plan, projectDoc = null) {
  return canUseBrandedClientPortal(plan, projectDoc);
}

/** Any tier may create a portal link; free/pack tiers show Powered by Volugraph. */
export function canCreateClientPortal(plan) {
  void plan;
  return true;
}

/** Architecture scene timelines (curated snapshots, viewer strip, share-as-timeline). Professional+ */
export function canUseSceneTimelines(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return hasProfessionalEntitlements(plan) || p === 'studio' || p === 'enterprise';
}

/** Frame search (keyframes + captions) — unlocked project or Enterprise. */
export function canUseFrameSearch(plan, projectDoc = null) {
  return canUseBrandedClientPortal(plan, projectDoc);
}

/** @deprecated use canUseFrameSearch */
export function canBuildSemanticIndex(plan) {
  return canUseFrameSearch(plan);
}

/** Max source-video file size for the given plan (technical ceiling; tier limits are duration-based). */
export function getMaxUploadBytes(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'enterprise') return SOURCE_VIDEO_UPLOAD_MAX_BYTES_ENTERPRISE;
  return SOURCE_VIDEO_UPLOAD_MAX_BYTES;
}

/** Human-readable limit for UI (e.g. "3 GB", "16 GB"). */
export function formatSingleFileUploadLimitLabel(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1024 * 1024 * 1024) {
    const gb = n / (1024 * 1024 * 1024);
    const rounded = gb >= 10 ? Math.round(gb) : Math.round(gb * 10) / 10;
    return `${rounded} GB`;
  }
  const mb = Math.max(1, Math.round(n / (1024 * 1024)));
  return `${mb} MB`;
}

/**
 * First file in the list that exceeds the per-file byte ceiling.
 * @param {Array<{ name?: string, size?: number }>} files
 * @param {number} maxBytes
 * @returns {{ name: string, size: number, maxBytes: number } | null}
 */
export function findUploadFileExceedingMaxBytes(files, maxBytes) {
  const ceiling = Number(maxBytes);
  if (!Number.isFinite(ceiling) || ceiling <= 0) return null;
  for (const file of files || []) {
    if (!file) continue;
    const size = typeof file.size === 'number' ? file.size : 0;
    if (size > ceiling) {
      return {
        name: String(file.name || 'file'),
        size,
        maxBytes: ceiling,
      };
    }
  }
  return null;
}

/** Modal / toast copy when a file exceeds getMaxUploadBytes (technical cap); tier limits are duration-based. */
export function buildFileTooLargeUserMessage(maxBytes, detail = {}) {
  const limitLabel = formatSingleFileUploadLimitLabel(maxBytes);
  const fileName = detail?.fileName ?? detail?.name ?? null;
  const fileSizeBytes = detail?.fileSizeBytes ?? detail?.size ?? null;
  const fileSizeLabel =
    fileSizeBytes != null && Number.isFinite(Number(fileSizeBytes))
      ? formatSingleFileUploadLimitLabel(Number(fileSizeBytes))
      : null;

  if (fileName && fileSizeLabel && limitLabel) {
    return `${fileName} is ${fileSizeLabel}. Maximum size per file is ${limitLabel}. Plan limits are based on video length. Compress or shorten your clip if needed.`;
  }
  if (limitLabel) {
    return `Maximum size per file is ${limitLabel}. Plan limits are based on video length. Compress or shorten your clip if needed.`;
  }
  return 'This file exceeds the maximum upload size. Plan limits are based on video length. Try a smaller or shorter file.';
}

/** Short plan name for in-sentence copy ("The Free Plan allows…"). */
export function getSubscriptionPlanDisplayNameForCopy(plan) {
  const p = normalizeSubscriptionPlan(plan);
  const map = {
    free: 'Free',
    capture_pack: 'Capture Packs',
    professional: 'Capture Packs',
    studio: 'Capture Packs',
    pay_per_capture: 'Capture Packs',
    enterprise: 'Enterprise',
    pro_delivery: 'Pro Delivery',
  };
  return map[p] ?? 'Free';
}

/** m:ss for modal copy (plans cap at hours today). */
function formatVideoDurationClock(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return null;
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** Modal body when a source video exceeds getMaxVideoDurationSecondsForPlan(plan). */
export function buildVideoTooLongUserMessage(maxSec, actualSec, plan) {
  const planName = getSubscriptionPlanDisplayNameForCopy(plan);
  const actualClock = formatVideoDurationClock(actualSec);
  let allowance = null;
  if (maxSec != null && Number.isFinite(maxSec) && maxSec > 0) {
    if (maxSec < 60) {
      allowance = `The ${planName} Plan allows up to ${Math.round(maxSec)} seconds of video.`;
    } else if (maxSec < 3600) {
      const m = Math.round(maxSec / 60);
      allowance = `The ${planName} Plan allows up to ${m} minute${m === 1 ? '' : 's'} of video.`;
    } else {
      const h = Math.round(maxSec / 3600);
      allowance = `The ${planName} Plan allows up to ${h} hour${h === 1 ? '' : 's'} of video.`;
    }
  }
  if (!allowance) {
    return 'This video is longer than your plan allows. Please trim your clip or upgrade.';
  }
  const tail = actualClock
    ? ` This video is ${actualClock}. Please trim your clip or upgrade.`
    : ' Please trim your clip or upgrade.';
  return `${allowance}${tail}`;
}

/** Human-readable max video duration for plan feature bullets and docs. */
export function formatMaxVideoDurationShortForPlan(plan) {
  const sec = getMaxVideoDurationSecondsForPlan(plan);
  if (!Number.isFinite(sec) || sec <= 0) return 'your plan limit';
  if (sec < 60) return `${Math.round(sec)} seconds`;
  if (sec < 3600) {
    const m = Math.round(sec / 60);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  const h = Math.round(sec / 3600);
  return `${h} hour${h === 1 ? '' : 's'}`;
}

/** Pricing card sub-bullet: "Up to 10 minutes per video upload". */
export function formatMaxVideoDurationPerUploadFeature(plan) {
  const label = formatMaxVideoDurationShortForPlan(plan);
  if (label === 'your plan limit') return 'Video length limited per plan';
  return `Up to ${label} per video upload`;
}

/** Top bar / hints: "1 MINUTE PER UPLOAD", "2 MINUTES PER UPLOAD", etc. */
export function formatMaxVideoDurationPerUploadHint(plan) {
  const sec = getMaxVideoDurationSecondsForPlan(plan);
  if (!Number.isFinite(sec) || sec <= 0) return 'VIDEO DURATION LIMITED PER PLAN';
  if (sec < 60) return `${Math.round(sec)} SECONDS PER UPLOAD`;
  if (sec < 3600) {
    const m = Math.round(sec / 60);
    return `${m} MINUTE${m === 1 ? '' : 'S'} PER UPLOAD`;
  }
  const h = Math.round(sec / 3600);
  return `${h} HOUR${h === 1 ? '' : 'S'} PER UPLOAD`;
}

/** True if the user doc has admin flag (unlimited captures, bypass limits). */
export function isAdminUser(userData) {
  if (!userData || typeof userData !== 'object') return false;
  const a = userData.admin;
  return a === true || a === 'Yes' || a === 'yes';
}

/** Plan tier for UI and client-side gates — admins always resolve to enterprise. */
export function effectivePlanForUser(user) {
  if (isAdminUser(user)) return 'enterprise';
  return normalizeSubscriptionPlan(user?.plan || 'free');
}

export function hasLifetimeCaptureLimit(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return p === 'free' || p === 'capture_pack';
}

export function getMonthlyCaptureLimit(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'capture_pack' || isPayPerCapturePlan(plan)) return null;
  if (p === 'professional') return PROFESSIONAL_MONTHLY_CAPTURE_LIMIT;
  if (p === 'studio') return STUDIO_MONTHLY_CAPTURE_LIMIT;
  return null;
}

export function isUnlimitedMonthlyCaptures(plan) {
  return normalizeSubscriptionPlan(plan) === 'enterprise';
}

export function getMaxVideoDurationSecondsForPlan(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (isShopifyPlan(p)) {
    return shopifyPlanMaxVideoDurationSec(resolveShopifyPlanId(p));
  }
  if (p === 'free') return FREE_PLAN_MAX_VIDEO_DURATION_SEC;
  return PAID_PLAN_MAX_VIDEO_DURATION_SEC;
}

/**
 * Total bytes allowed under users/{uid}/studio/.../captures (display / client-side hints).
 * @param {string} plan
 * @returns {number} bytes
 */
export function getStorageCapBytesForPlan(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'free') return FREE_STORAGE_CAP_BYTES;
  if (hasProfessionalEntitlements(p)) return PROFESSIONAL_STORAGE_CAP_BYTES;
  if (p === 'pro_delivery') return PROFESSIONAL_STORAGE_CAP_BYTES;
  if (p === 'studio') return STUDIO_STORAGE_CAP_BYTES;
  if (p === 'enterprise') return ENTERPRISE_STORAGE_CAP_BYTES;
  return FREE_STORAGE_CAP_BYTES;
}

/**
 * Effective capture storage cap (bytes). Honors `users/{uid}/billing/plan.storageCapOverrideBytes` when set (ops).
 * @param {Record<string, unknown> | null | undefined} billingPlanData - data() from billing/plan or partial
 * @param {unknown} planRaw - subscription.plan from user or billing plan doc
 * @returns {number}
 */
export function getStorageCapBytesForUserDoc(billingPlanData, planRaw) {
  const raw = billingPlanData?.storageCapOverrideBytes;
  const override = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (Number.isFinite(override) && override > 0) {
    return Math.floor(override);
  }
  return getStorageCapBytesForPlan(planRaw);
}
